import PropTypes from "prop-types"

/**
 * PropTypes for the OCPI Credentials SidebarSection component.
 *
 * @property {Object|null} selectedCredential - Currently selected credential row (null if no selection)
 * @property {Object} filters - Current filter state
 * @property {Function} onFilterChange - Filter change dispatcher
 * @property {number} visibleCount - Number of credentials after filtering
 * @property {Array<Object>} credentials - All credentials data, used to build the role filter's options
 * @property {Object|null} status - Integration status payload, source of the modules list
 * @property {boolean} canRegister - Whether the caller may see the Actions tab (admin-only)
 * @property {Function} onOpenRegisterModal - Opens the register/renew confirmation modal
 * @property {Object|null} hubCredentials - Credentials read from the hub, shown in the details tab
 * @property {boolean} isReadingHub - Whether the hub read is in flight
 * @property {Function} onReadHub - Reads the credentials MOBI.E holds for this operator
 * @property {Function} onRenewToken - Opens the renew token confirmation
 * @property {Function} onUnregister - Opens the unregister confirmation
 * @property {Function} onChangePublicUrl - Opens the public URL form
 */
export const ocpiCredentialsSidebarSectionPropTypes = {
	selectedCredential: PropTypes.object,
	filters: PropTypes.object.isRequired,
	onFilterChange: PropTypes.func.isRequired,
	visibleCount: PropTypes.number.isRequired,
	credentials: PropTypes.array.isRequired,
	status: PropTypes.object,
	canRegister: PropTypes.bool,
	onOpenRegisterModal: PropTypes.func,
	hubCredentials: PropTypes.object,
	isReadingHub: PropTypes.bool,
	onReadHub: PropTypes.func,
	onRenewToken: PropTypes.func,
	onUnregister: PropTypes.func,
	onChangePublicUrl: PropTypes.func,
}

/**
 * PropTypes for the confirmation modal of the registration actions.
 *
 * @property {boolean} isOpen - Whether the modal is open
 * @property {Function} onClose - Closes the modal
 * @property {string} title - Modal title
 * @property {string} body - Explanation shown before confirming
 * @property {string} confirmLabel - Label of the confirm button
 * @property {string} appearance - Button appearance of the confirm button
 * @property {string} [confirmPhrase] - Phrase the user must type before confirming, none when missing
 * @property {Function} onConfirm - Runs the action
 * @property {boolean} isSubmitting - Whether the request is in flight
 */
export const confirmActionModalPropTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	title: PropTypes.string.isRequired,
	body: PropTypes.string.isRequired,
	confirmLabel: PropTypes.string.isRequired,
	appearance: PropTypes.string,
	confirmPhrase: PropTypes.string,
	onConfirm: PropTypes.func.isRequired,
	isSubmitting: PropTypes.bool.isRequired,
}

/**
 * PropTypes for the credentials read from the hub.
 *
 * @property {Object} hubCredentials - Hub Credentials object (`url`, redacted `token`, `roles`)
 */
export const hubCredentialsSectionPropTypes = {
	hubCredentials: PropTypes.object.isRequired,
}

/**
 * PropTypes for the register/renew confirmation modal.
 *
 * @property {boolean} isOpen - Whether the modal is open
 * @property {Function} onClose - Closes the modal
 * @property {boolean} isRegistered - true renders the renew copy, false the register copy
 * @property {string} versionsUrl - Current versionsUrl input value
 * @property {Function} onVersionsUrlChange - versionsUrl input change handler
 * @property {string} credentialsTokenA - Current credentialsTokenA input value (register only)
 * @property {Function} onCredentialsTokenAChange - credentialsTokenA input change handler
 * @property {Function} onConfirm - Submits the register/renew request
 * @property {boolean} isSubmitting - Whether the request is in flight
 */
export const registerCredentialsModalPropTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	isRegistered: PropTypes.bool.isRequired,
	versionsUrl: PropTypes.string.isRequired,
	onVersionsUrlChange: PropTypes.func.isRequired,
	credentialsTokenA: PropTypes.string.isRequired,
	onCredentialsTokenAChange: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	isSubmitting: PropTypes.bool.isRequired,
}

/**
 * PropTypes for OcpiCredentials view.
 * No external props are expected; kept for consistency across view definitions.
 */
export const ocpiCredentialsViewPropTypes = {}

/**
 * PropTypes for the public URL form.
 *
 * @property {boolean} isOpen - Whether the modal is open
 * @property {Function} onClose - Closes the modal
 * @property {string|null} currentUrl - Public URL declared to MOBI.E today, empty when unset
 * @property {string} publicUrl - Current input value
 * @property {Function} onPublicUrlChange - Input change handler
 * @property {Function} onConfirm - Sends the new URL
 * @property {boolean} isSubmitting - Whether the request is in flight
 */
export const changePublicUrlModalPropTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	currentUrl: PropTypes.string,
	publicUrl: PropTypes.string.isRequired,
	onPublicUrlChange: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	isSubmitting: PropTypes.bool.isRequired,
}
